import type { PaintExportFormat } from '@/features/online-paint/model/paint'

export class PaintExportError extends Error {
  constructor(public readonly code: 'encode_failed' | 'unsupported_format' | 'download_failed') {
    super(code)
  }
}

interface PaintDownloadAnchor {
  href: string
  download: string
  click: () => void
  remove: () => void
}

export interface PaintDownloadEnvironment {
  createObjectUrl: (blob: Blob) => string
  revokeObjectUrl: (url: string) => void
  createAnchor: () => PaintDownloadAnchor
  appendAnchor: (anchor: PaintDownloadAnchor) => void
}

const browserDownloadEnvironment: PaintDownloadEnvironment = {
  createObjectUrl: (blob) => URL.createObjectURL(blob),
  revokeObjectUrl: (url) => URL.revokeObjectURL(url),
  createAnchor: () => document.createElement('a'),
  appendAnchor: (anchor) => document.body.appendChild(anchor as HTMLAnchorElement),
}

export function downloadPaintBlob(
  blob: Blob,
  filename: string,
  environment: PaintDownloadEnvironment = browserDownloadEnvironment,
): void {
  let url: string | null = null
  let anchor: PaintDownloadAnchor | null = null
  try {
    url = environment.createObjectUrl(blob)
    anchor = environment.createAnchor()
    anchor.href = url
    anchor.download = filename
    environment.appendAnchor(anchor)
    anchor.click()
  } catch {
    throw new PaintExportError('download_failed')
  } finally {
    try {
      anchor?.remove()
    } catch {
      // Best-effort DOM cleanup must not hide the original download failure.
    }
    if (url) {
      try {
        environment.revokeObjectUrl(url)
      } catch {
        // Best-effort URL cleanup must not change the operation outcome.
      }
    }
  }
}

export async function encodePaintCanvas(
  canvas: HTMLCanvasElement,
  format: PaintExportFormat,
): Promise<Blob> {
  let blob: Blob | null
  try {
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, format, 0.92))
  } catch {
    throw new PaintExportError('encode_failed')
  }
  if (!blob) throw new PaintExportError('encode_failed')
  if (blob.type !== format) throw new PaintExportError('unsupported_format')
  return blob
}
